import * as Sentry from "@sentry/solid";

/**
 * FE error tracking + web vitals. Inert unless VITE_SENTRY_DSN is set at
 * build time (mirrors the BE's Z01_SENTRY_DSN convention), so local dev
 * and test builds never phone home.
 *
 * browserTracingIntegration emits a pageload/navigation transaction per
 * route change carrying the web vitals (LCP, CLS, FCP, TTFB, INP) plus
 * fetch spans. Web vitals only flow when BOTH the DSN and a non-zero
 * VITE_SENTRY_TRACES_SAMPLE_RATE are set; errors flow with the DSN alone.
 *
 * Because API calls are same-origin (/api/* through the Vercel rewrite),
 * the default trace propagation attaches sentry-trace headers and the
 * BE's sentry-tower layer continues the trace — one distributed trace
 * from browser click to vLLM stream-open.
 *
 * Do NOT set tracePropagationTargets: the default (same-origin only) is
 * what keeps sentry-trace/baggage headers OFF the cross-origin ZITADEL
 * calls (auth.z01.ai) — a pattern matching that origin would fail the
 * OIDC token exchange with a CORS preflight rejection.
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) {
    return;
  }
  Sentry.init({
    dsn,
    environment: import.meta.env.PROD ? "production" : "development",
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: tracesSampleRate(
      import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE as string | undefined,
    ),
  });
}

/** Defensive parse, mirroring the BE: unset/garbage = 0, clamped to [0, 1]. */
export function tracesSampleRate(raw: string | undefined): number {
  const parsed = Number.parseFloat(raw ?? "");
  if (Number.isNaN(parsed)) {
    return 0;
  }
  return Math.min(1, Math.max(0, parsed));
}
